<template>
  <button
    :disabled="disabled"
    :type="type"
    class="inline-flex items-center justify-center gap-2 rounded-lg border focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    :class="[buttonSize, buttonType, textColor, bgColor, extraClass]"
  >
    <img
      v-if="imagePath"
      :src="cdn(imagePath)"
      :class="[buttonImageSize, buttonImagePosition,imageClass]"
      alt=""
    >
    <Loading
      v-if="iconName && iconName == 'Loading' && disabled"
      color="text-primary-900"
      height="16"
      width="16"
    />
    <component
      :is="iconComponent(iconType, iconName)"
      v-if="iconName"
      :class="[buttonImageSize, buttonImagePosition, iconColor, iconSize]"
      class="size-4"
    />
    <component
      :is="iconComName"
      v-if="isIconComponent"
      class="size-4"
    />
    <template v-if="$slots.default">
      <slot />
    </template>
    <span
      v-else-if="btnTitle"
      class="text-nowrap"
      :class="titleClass"
    >{{ btnTitle }}</span>
  </button>
</template>
<script>

import Loading from '@/components/Loading.vue';
import ButtonLoading from '@/components/ButtonLoading.vue';
import ScannerIcon from "@/components/UpdatedDesignVersion/ImageComponents/SiteEdit/ScannerIcon.vue";
import RocketIcon from "@/components/UpdatedDesignVersion/ImageComponents/ConnectSiteUpdate/RocketIcon.vue";
import StagingIcon from "@/Common/Icons/NavMenu/StagingIcon.vue";
import * as OutlineIcons from "@heroicons/vue/outline";
import * as SolidIcons from "@heroicons/vue/solid";

export default {
  components: {
    Loading,
    ButtonLoading,
    ScannerIcon,
    StagingIcon,
    RocketIcon
  },

  props: {
    btnTitle: {
      type: String,
      default: '',
      required: false
    },
    btnType: {
      type: String,
      default: 'secondary',
      required: false
    },
    iconName: {
      type: String,
      default: '',
      required: false
    },
    type: {
      type: String,
      default: 'button',
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      default: 'btn-sm',
      required: false
    },
    imagePath: {
      type: String,
      required: false,
      default: ''
    },
    iconType: {
      type: String,
      required: false,
      default: 'outline'
    },
    iconPosition: {
      type: String,
      required: false
    },
    iconColor: {
      type: String,
      default: ''
    },
    textColor: {
      type: String,
      default: ''
    },
    bgColor: {
      type: String,
      default: ''
    },
    iconSize: {
      type: String,
      default: ''
    },
    extraClass: {
      type: String,
      default: ''
    },
    isIconComponent: {
      type: Boolean,
      default: false
    },
    iconComName: {
      type: String,
      default: ''
    },
    titleClass: {
      type: String,
      default: ''
    },
    imageClass: {
      type: String,
      default: ''
    }

  },
  data() {
    return {
      buttonType: null,
      buttonSize: null,
      buttonImageSize: null,
      buttonImagePosition: null,
      defaultButton: 'text-white border-transparent',
      outlineButton: 'bg-white shadow-sm',
      textButton: 'bg-transparent border-none focus:ring-transparent !p-0 !h-auto'
    };
  },
  created() {
    switch (this.btnType) {
      case 'primary':
        this.buttonType = 'bg-primary-900 transition-colors duration-200 ease-linear hover:bg-primary-1300 focus:bg-grayCust-430 ' + this.defaultButton;
        break;
      case 'secondary':
        this.buttonType = 'bg-secondary-800 transition-colors duration-200 ease-linear hover:bg-primary-310 focus:bg-primary-900 ' + this.defaultButton;
        break;
      case 'danger':
        this.buttonType = 'bg-redCust-730 transition-colors duration-200 ease-linear hover:bg-pinkCust-150 focus:bg-redCust-720  ' + this.defaultButton;
        break;
      case 'gray-outline-btn':
        this.buttonType = 'text-grayCust-980 border-grayCust-160 hover:bg-grayCust-50 hover:text-grayCust-1740 transition-colors duration-200 ease-linear focus:bg-grayCust-100 ' + this.outlineButton;
        break;
      case 'danger-outline-btn':
        this.buttonType = 'text-redCust-730 border-redCust-220 hover:bg-redCust-110 hover:text-pinkCust-150 transition-colors duration-200 ease-linear focus:bg-redCust-195 ' + this.outlineButton;
        break;
      case 'info-outline-btn':
        this.buttonType = 'text-blueCust-780 border-blueCust-610 focus:ring-blueCust-280 hover:bg-blueCust-65 hover:text-blueCust-900' + this.outlineButton;
        break;
      case 'gray-text-btn':
        this.buttonType = 'text-grayCust-980 hover:text-grayCust-1740 ' + this.textButton;
        break;
      case 'secondary-text-btn':
        this.buttonType = 'text-secondary-800 hover:opacity-90 ' + this.textButton;
        break;
      case 'primary-text-btn':
        this.buttonType = 'text-primary-900 hover:opacity-90 ' + this.textButton;
        break;
      case 'danger-text-btn':
        this.buttonType = 'text-redCust-730 hover:text-pinkCust-150 ' + this.textButton;
        break;
    }
    switch (this.size) {
      case 'btn-sm':
        this.buttonSize = 'h-9 px-3.5 py-2 text-sm font-medium';
        this.buttonImageSize = 'min-w-[18px] h-[18px]';
        break;
      case 'btn-md':
        this.buttonSize = 'h-10 px-4 py-2.5 text-sm font-medium';
        this.buttonImageSize = 'w-5 h-5';
        break;
      case 'btn-lg':
        this.buttonSize = 'h-11 px-[18px] py-2.5 text-base font-semibold';
        this.buttonImageSize = 'w-5 h-5';
        break;
      case 'btn-xl':
        this.buttonSize = 'h-12 px-5 py-3 text-base font-semibold';
        this.buttonImageSize = 'w-5 h-5';
        break;
      case 'btn-xxl':
        this.buttonSize = 'h-[60px] px-7 py-4 text-lg font-semibold';
        this.buttonImageSize = 'w-6 h-6';
        break;
    }
    switch (this.iconPosition) {
      case 'first':
        this.buttonImagePosition = 'order-first'
        break;
      case 'last':
        this.buttonImagePosition = 'order-last'
        break;
    }
  },
  methods: {
    iconComponent(type, iconName) {
      switch (type) {
        case "solid":
          if (Object.prototype.hasOwnProperty.call(SolidIcons, iconName)) {
            return SolidIcons[iconName];
          } else {
            return null;
          }
        case "outline":
          if (Object.prototype.hasOwnProperty.call(OutlineIcons, iconName)) {
            return OutlineIcons[iconName];
          } else {
            return null;
          }
        default:
          return null;
      }
    },
  }
};
</script>